import React, { useState, useEffect } from "react";
import { 
  Form, Row, Col, Alert
} from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUserPlus, faCalendarPlus, faTimes, faCheck,
  faClock, faUser, faCalendarAlt, faExclamationTriangle
} from "@fortawesome/free-solid-svg-icons";
import { motion, AnimatePresence } from "framer-motion";
import styled from "styled-components";
import { collection, addDoc, Timestamp } from "firebase/firestore";
import { db } from "../components/firebase-config.js";
import { getAuth } from "firebase/auth";

// Réutilisation des styles existants
const StyledCard = styled(motion.div)`
  background: white;
  border-radius: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  border: none;
  margin-bottom: 2rem;
  
  &:hover {
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
    transform: translateY(-5px);
  }
  
  @media (max-width: 768px) {
    border-radius: 16px;
    margin-bottom: 1rem;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
    
    &:hover {
      transform: none;
    }
  }
`;

const CardHeader = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: linear-gradient(to right, #f8f9fa, white);
  
  @media (max-width: 768px) {
    padding: 1rem;
    flex-direction: column;
    align-items: flex-start;
    
    h5 {
      margin-bottom: 0.5rem;
    }
  }
`;

const CardBody = styled.div`
  padding: 2rem;
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const StyledButton = styled(motion.button)`
  border-radius: 16px;
  padding: 0.75rem 1.5rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.95rem;
  
  &.primary {
    background: linear-gradient(-45deg, #6366f1, #8b5cf6, #3b82f6);
    background-size: 200% 200%;
    color: white;
    box-shadow: 0 4px 15px rgba(99, 102, 241, 0.2);
    
    &:hover {
      box-shadow: 0 6px 20px rgba(99, 102, 241, 0.3);
      transform: translateY(-2px);
    }
  }
  
  &.secondary {
    background: #f8f9fa;
    color: #4b5563;
    
    &:hover {
      background: #e9ecef;
      transform: translateY(-2px);
    }
  }
  
  @media (max-width: 768px) {
    padding: 0.6rem 1.2rem;
    font-size: 0.9rem;
    border-radius: 12px;
    
    &:hover {
      transform: none;
    }
  }
  
  &.mobile-full {
    @media (max-width: 768px) {
      width: 100%;
      justify-content: center;
    }
  }
`;

const TimeSlotGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 12px;
  margin-top: 1.5rem;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
  }
  
  @media (max-width: 480px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const TimeSlot = styled(motion.div)`
  padding: 1rem;
  border-radius: 16px;
  text-align: center;
  cursor: pointer;
  position: relative;
  font-weight: 600;
  transition: all 0.3s ease;
  
  &.disponible {
    background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%);
    color: #059669;
    border: 1px solid #a7f3d0;
  }
  
  &.partiel {
    background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%);
    color: #d97706;
    border: 1px solid #fcd34d;
  }
  
  &.complet {
    background: linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%);
    color: #6b7280;
    border: 1px solid #e5e7eb;
    cursor: not-allowed;
    opacity: 0.7;
  }
  
  &.selected {
    border: 2px solid #6366f1;
    box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.2);
  }
  
  .slot-info {
    font-size: 0.8rem;
    margin-top: 0.5rem;
    opacity: 0.9;
  }
  
  @media (max-width: 768px) {
    padding: 0.75rem 0.5rem;
    border-radius: 12px;
    font-size: 0.9rem;
    
    .slot-info {
      font-size: 0.7rem;
    }
  }
`;

const FormSection = styled.div`
  margin-bottom: 2rem;
  padding-bottom: 2rem;
  border-bottom: 1px solid #e5e7eb;
  
  &:last-child {
    border-bottom: none;
    margin-bottom: 0;
    padding-bottom: 0;
  }
  
  h6 {
    display: flex;
    align-items: center;
    margin-bottom: 1.5rem;
    color: #4b5563;
    font-weight: 600;
    
    svg {
      margin-right: 0.75rem;
      color: #6366f1;
    }
  }
`;

const ProgressSteps = styled.div`
  display: flex;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    overflow-x: auto;
    padding-bottom: 1rem;
    margin-bottom: 1rem;
  }
`;

const Step = styled.div`
  flex: 1;
  text-align: center;
  position: relative;
  
  &:not(:last-child)::after {
    content: '';
    position: absolute;
    top: 15px;
    left: 50%;
    width: 100%;
    height: 2px;
    background-color: #e5e7eb;
    z-index: 1;
  }
  
  &.active .step-number {
    background: linear-gradient(-45deg, #6366f1, #8b5cf6, #3b82f6);
    color: white;
  }
  
  &.completed .step-number {
    background: #059669;
    color: white;
  }
  
  &.active:not(:last-child)::after, &.completed:not(:last-child)::after {
    background: linear-gradient(to right, #6366f1, #8b5cf6);
  }
  
  .step-number {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background-color: #f3f4f6;
    color: #6b7280;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 0.5rem;
    position: relative;
    z-index: 2;
    font-weight: bold;
  }
  
  .step-label {
    font-size: 0.85rem;
    color: #6b7280;
    font-weight: 500;
  }
  
  &.active .step-label {
    color: #4b5563;
    font-weight: 600;
  }
  
  @media (max-width: 768px) {
    min-width: 100px;
    
    .step-number {
      width: 28px;
      height: 28px;
      font-size: 0.9rem;
    }
    
    .step-label {
      font-size: 0.75rem;
    }
  }
`;

const AuxiliaireRapide = ({ 
  auxiliaireData, 
  medecins = [],
  onClose, 
  onSuccess, 
  checkCreneauxDisponibles,
  today
}) => {
  // États pour le formulaire combiné
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [creneauxDisponibles, setCreneauxDisponibles] = useState([]);
  
  // État pour le patient
  const [patientForm, setPatientForm] = useState({
    nom: "",
    dateNaissance: "",
    email: "",
    telephone: "",
    assurances: []
  });
  
  // État pour le rendez-vous
  const [rdvForm, setRdvForm] = useState({
    medecinId: "",
    heure: "",
    duree: 15,
    motif: "",
    statut: "confirmé"
  });
  
  const auth = getAuth();
  
  // Fonction pour gérer les changements dans le formulaire de patient
  const handlePatientChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name === "assurances") {
      const updatedArray = [...patientForm.assurances];
      if (checked) {
        updatedArray.push(value);
      } else {
        const index = updatedArray.indexOf(value);
        if (index > -1) {
          updatedArray.splice(index, 1);
        }
      }
      setPatientForm({ ...patientForm, [name]: updatedArray });
    } else {
      setPatientForm({ ...patientForm, [name]: value });
    }
  };
  
  // Fonction pour gérer les changements dans le formulaire de rendez-vous
  const handleRdvChange = (e) => {
    const { name, value } = e.target;
    setRdvForm({ ...rdvForm, [name]: value });
  };
  
  // Fonction pour gérer le changement de médecin - CORRIGÉE
  const handleMedecinChange = (e) => {
    const medecinId = e.target.value;
    setRdvForm({
      ...rdvForm,
      medecinId,
      heure: "" // Réinitialiser l'heure sélectionnée
    });
  };
  
  // Utiliser useEffect pour mettre à jour les créneaux lorsque le médecin change
  useEffect(() => {
    if (rdvForm.medecinId) {
      const creneaux = checkCreneauxDisponibles(rdvForm.medecinId);
      setCreneauxDisponibles(creneaux);
    } else {
      setCreneauxDisponibles([]);
    }
  }, [rdvForm.medecinId, checkCreneauxDisponibles]);
  
  // Fonction pour valider le formulaire de patient
  const validatePatientForm = () => {
    if (!patientForm.telephone) {
      setMessage("Veuillez remplir tous les champs obligatoires du patient.");
      return false;
    }
    return true;
  };
  
  // Fonction pour valider le formulaire de rendez-vous
  const validateRdvForm = () => {
    if (!rdvForm.medecinId || !rdvForm.heure) {
      setMessage("Veuillez sélectionner un médecin et un créneau horaire.");
      return false;
    }
    return true;
  };
  
  // Fonction pour passer à l'étape suivante
  const goToNextStep = () => {
    if (currentStep === 1) {
      if (validatePatientForm()) {
        setCurrentStep(2);
        setMessage("");
      }
    }
  };
  
  // Fonction pour revenir à l'étape précédente
  const goToPreviousStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      setMessage("");
    }
  };
  
  // Fonction pour soumettre le formulaire complet
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (!validatePatientForm() || !validateRdvForm()) {
        return;
      }
      
      setLoading(true);
      
      // 1. Ajouter le patient
      const patientData = {
        ...patientForm,
        structureId: auxiliaireData.structureId,
        createdAt: new Date(),
        createdBy: {
          id: auth.currentUser.uid,
          type: "auxiliaire",
          nom: `${auxiliaireData.nom}`
        }
      };
      
      const patientDocRef = await addDoc(collection(db, "patients"), patientData);
      const patientId = patientDocRef.id;
      
      // 2. Trouver le médecin sélectionné
      const medecin = medecins.find(m => m.id === rdvForm.medecinId);
      
      // 3. Ajouter le rendez-vous
      const rdvData = {
        ...rdvForm,
        patientId,
        date: today,
        structureId: auxiliaireData.structureId,
        medecinNom: medecin ? `Dr. ${medecin.nom}` : "",
        medecinSpecialite: medecin ? medecin.specialite : "",
        patientNom: `${patientForm.nom}`,
        patientTelephone: patientForm.telephone,
        patientEmail: patientForm.email,
        createdAt: new Date(),
        createdBy: {
          id: auth.currentUser.uid,
          type: "auxiliaire",
          nom: ` ${auxiliaireData.nom}`
        },
        priorite: "normal"
      };
      
      const rdvDocRef = await addDoc(collection(db, "rendezvous"), rdvData);
      
      // 4. Envoyer une notification au patient si configuré
      if (patientForm.telephone) {
        try {
          // Vous pouvez implémenter l'envoi de notification ici si nécessaire
          console.log("Notification envoyée au patient");
        } catch (notifError) {
          console.error("Erreur lors de l'envoi de la notification:", notifError);
        }
      }
      
      setLoading(false);
      onSuccess({
        patient: { id: patientId, ...patientData },
        rdv: { id: rdvDocRef.id, ...rdvData }
      });
      
    } catch (error) {
      setMessage(`Erreur: ${error.message}`);
      setLoading(false);
    }
  };
  
  return (
    <StyledCard>
      <CardHeader>
        <h5 className="mb-0 fw-bold">
          <FontAwesomeIcon icon={faUserPlus} className="me-2 text-primary" />
          Nouveau patient avec rendez-vous
        </h5>
      </CardHeader>
      <CardBody>
        <ProgressSteps>
          <Step className={currentStep >= 1 ? "active" : ""}>
            <div className="step-number">1</div>
            <div className="step-label">Informations patient</div>
          </Step>
          <Step className={currentStep >= 2 ? "active" : ""}>
            <div className="step-number">2</div>
            <div className="step-label">Rendez-vous</div>
          </Step>
          <Step className={currentStep >= 3 ? "active" : ""}>
            <div className="step-number">3</div>
            <div className="step-label">Confirmation</div>
          </Step>
        </ProgressSteps>
        
        {message && (
          <Alert 
            variant={message.includes("Erreur") ? "danger" : "warning"} 
            dismissible
            onClose={() => setMessage("")}
            className="mb-4 shadow-sm rounded-3"
          >
            <div className="d-flex align-items-center">
              <FontAwesomeIcon 
                icon={faExclamationTriangle} 
                className="me-3"
              />
              <div>{message}</div>
            </div>
          </Alert>
        )}
        
        <Form onSubmit={handleSubmit}>
          <AnimatePresence mode="wait">
            {currentStep === 1 && (
              <motion.div
                key="step1"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                transition={{ duration: 0.3 }}
              >
                <FormSection>
                  <h6>
                    <FontAwesomeIcon icon={faUser} />
                    Informations du patient
                  </h6>
                  <Row>
                    <Col md={6} className="mb-4">
                      <Form.Group>
                        <Form.Label className="fw-bold">Nom <span className="text-danger">(Optionnel)</span></Form.Label>
                        <Form.Control
                          type="text"
                          name="nom"
                          value={patientForm.nom}
                          onChange={handlePatientChange}
                          required
                          className="rounded-pill py-2 shadow-sm"
                          placeholder="Nom de famille"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6} className="mb-4">
                      <Form.Group>
                        <Form.Label className="fw-bold">Date de naissance <span className="text-danger">(Optionnel)</span></Form.Label>
                        <Form.Control
                          type="date"
                          name="dateNaissance"
                          value={patientForm.dateNaissance}
                          onChange={handlePatientChange}
                          required
                          className="rounded-pill py-2 shadow-sm"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  
                  <Row>
                   
                    <Col md={6} className="mb-4">
                      <Form.Group>
                        <Form.Label className="fw-bold">Téléphone <span className="text-danger">(Obligatoire)</span></Form.Label>
                        <Form.Control
                          type="tel"
                          name="telephone"
                          value={patientForm.telephone}
                          onChange={handlePatientChange}
                          required
                          className="rounded-pill py-2 shadow-sm"
                          placeholder="Numéro de téléphone"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  
                  <Form.Group className="mb-4">
                    <Form.Label className="fw-bold">Email <span className="text-danger">(Optionnel)</span></Form.Label>
                    <Form.Control
                      type="email"
                      name="email"
                      value={patientForm.email}
                      onChange={handlePatientChange}
                      className="rounded-pill py-2 shadow-sm"
                      placeholder="Adresse email"
                    />
                  </Form.Group>
                </FormSection>
                
                <div className="d-flex justify-content-between">
                  <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
                    <StyledButton 
                      type="button"
                      className="secondary"
                      onClick={onClose}
                    >
                      <FontAwesomeIcon icon={faTimes} className="me-2" />
                      Annuler
                    </StyledButton>
                  </motion.div>
                  <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
                    <StyledButton 
                      type="button"
                      className="primary"
                      onClick={goToNextStep}
                    >
                      Suivant
                      <FontAwesomeIcon icon={faCalendarAlt} className="ms-2" />
                    </StyledButton>
                  </motion.div>
                </div>
              </motion.div>
            )}
            
            {currentStep === 2 && (
              <motion.div
                key="step2"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                transition={{ duration: 0.3 }}
              >
                <FormSection>
                  <h6>
                    <FontAwesomeIcon icon={faCalendarAlt} />
                    Détails du rendez-vous
                  </h6>
                  <Row>
                    <Col md={12} className="mb-4">
                      <Form.Group>
                        <Form.Label className="fw-bold">Médecin <span className="text-danger">*</span></Form.Label>
                        <Form.Select
                          name="medecinId"
                          value={rdvForm.medecinId}
                          onChange={handleMedecinChange}
                          required
                          className="rounded-pill py-2 shadow-sm"
                        >
                          <option value="">Sélectionner un médecin</option>
                          {medecins.map((medecin) => (
                            <option key={medecin.id} value={medecin.id}>
                              Dr. {medecin.nom} - {medecin.specialite}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  </Row>
                  
                  <Form.Group className="mb-4">
                    <Form.Label className="fw-bold">Créneau horaire <span className="text-danger">*</span></Form.Label>
                    {rdvForm.medecinId ? (
                      creneauxDisponibles.length > 0 ? (
                        <TimeSlotGrid>
                          {creneauxDisponibles.map((creneau) => (
                            <TimeSlot
                              key={creneau.heure}
                              className={`${creneau.statut} ${
                                rdvForm.heure === creneau.heure ? "selected" : ""
                              }`}
                              onClick={() => {
                                if (creneau.estDisponible) {
                                  setRdvForm({
                                    ...rdvForm,
                                    heure: creneau.heure,
                                  });
                                }
                              }}
                              whileHover={creneau.estDisponible ? { scale: 1.05, y: -3 } : {}}
                              whileTap={creneau.estDisponible ? { scale: 0.95 } : {}}
                              transition={{ duration: 0.2 }}
                            >
                              {creneau.heure}
                              {creneau.statut === "partiel" && (
                                <div className="slot-info">
                                  {creneau.nombreRdv}/3 occupé
                                  {creneau.nombreRdv > 1 ? "s" : ""}
                                </div>
                              )}
                              {creneau.statut === "complet" && (
                                <div className="slot-info">Complet</div>
                              )}
                            </TimeSlot>
                          ))}
                        </TimeSlotGrid>
                      ) : (
                        <Alert variant="warning" className="rounded-4 border-0 shadow-sm">
                          <div className="d-flex align-items-center">
                            <motion.div
                              animate={{ 
                                rotate: [0, 10, -10, 10, 0],
                              }}
                              transition={{ 
                                duration: 0.5,
                                repeat: 1,
                                repeatDelay: 1
                              }}
                              className="me-3"
                            >
                              <FontAwesomeIcon icon={faExclamationTriangle} className="fs-4" />
                            </motion.div>
                            <div>
                              <strong>Aucun créneau disponible</strong>
                              <p className="mb-0">Ce médecin n'a pas de disponibilité pour aujourd'hui.</p>
                            </div>
                          </div>
                        </Alert>
                      )
                    ) : (
                      <Alert variant="info" className="rounded-4 border-0 shadow-sm">
                        <div className="d-flex align-items-center">
                          <motion.div
                            animate={{ y: [0, -5, 0] }}
                            transition={{ 
                              duration: 1,
                              repeat: Infinity,
                              repeatType: "reverse"
                            }}
                            className="me-3"
                          >
                            <FontAwesomeIcon icon={faCalendarAlt} className="fs-4" />
                          </motion.div>
                          <div>
                            <strong>Sélectionnez un médecin</strong>
                            <p className="mb-0">Les créneaux disponibles s'afficheront ici.</p>
                          </div>
                        </div>
                      </Alert>
                    )}
                  </Form.Group>
                  
                  <Form.Group className="mb-4">
                    <Form.Label className="fw-bold">Motif</Form.Label>
                    <Form.Control
                      as="textarea"
                      name="motif"
                      value={rdvForm.motif}
                      onChange={handleRdvChange}
                      rows={3}
                      className="rounded-3 shadow-sm"
                      placeholder="Décrivez brièvement le motif de la consultation..."
                    />
                  </Form.Group>
                </FormSection>
                
                <div className="d-flex justify-content-between">
                  <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
                    <StyledButton 
                      type="button"
                      className="secondary"
                      onClick={goToPreviousStep}
                    >
                      <FontAwesomeIcon icon={faUser} className="me-2" />
                      Retour
                    </StyledButton>
                  </motion.div>
                  <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
                    <StyledButton 
                      type="submit"
                      className="primary"
                      disabled={!rdvForm.medecinId || !rdvForm.heure || loading}
                    >
                      {loading ? (
                        <>
                          <FontAwesomeIcon icon={faClock} className="me-2 fa-spin" />
                          Traitement en cours...
                        </>
                      ) : (
                        <>
                          <FontAwesomeIcon icon={faCheck} className="me-2" />
                          Enregistrer
                        </>
                      )}
                    </StyledButton>
                  </motion.div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </Form>
      </CardBody>
    </StyledCard>
  );
};

export default AuxiliaireRapide;
